// frontend-react/src/pages/LoginPage.js (ΠΛΗΡΕΣ ΚΑΙ ΤΕΛΙΚΟ)

import React, { useState, useContext, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { loginUser, registerUser, verify2FACode, verify2FARecoveryCode } from '../api/api';
import { FiUser, FiLock, FiMail, FiShield } from 'react-icons/fi';
import './LoginPage.css';

const GoogleIcon = () => (
    <svg style={{ width: '20px', height: '20px', marginRight: '12px' }} viewBox="0 0 24 24">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"></path><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"></path><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
    </svg>
);

const LoginPage = () => {
    const [view, setView] = useState('login');
    const [usernameOrEmail, setUsernameOrEmail] = useState('');
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [tempToken, setTempToken] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const { login } = useContext(AuthContext);

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    useEffect(() => {
        const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
        const googleError = searchParams.get('error');
        const requestedView = searchParams.get('view');
        if (!token && requestedView === 'register') setView('register');

        if (token) {
            window.history.replaceState(null, '', window.location.pathname);
            login(token).then(() => navigate('/today', { replace: true }));
        } else if (googleError) {
            setError(googleError === 'registration_required'
                ? 'Δημιούργησε πρώτα λογαριασμό και αποδέξου τους όρους. Μετά μπορείς να χρησιμοποιήσεις Google για σύνδεση.'
                : 'Η σύνδεση με Google απέτυχε. Δοκίμασε ξανά.');
            navigate('/login', { replace: true });
        }
    }, [searchParams, login, navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        try {
            const response = await loginUser(usernameOrEmail, password);
            if (response.data.status === '2fa_required') {
                setTempToken(response.data.temp_token);
                setView('2fa');
            } else {
                await login(response.data.access_token);
                navigate('/today');
            }
        } catch (err) { setError('Login failed. Please check your credentials.'); }
    };

    const handle2FAVerification = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const isRecovery = twoFactorCode.length > 6;
            const response = isRecovery
                ? await verify2FARecoveryCode(tempToken, twoFactorCode)
                : await verify2FACode(tempToken, twoFactorCode);
            await login(response.data.access_token);
            navigate('/today');
        } catch (err) { setError(err.response?.data?.detail || 'Invalid code.'); }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        if (!termsAccepted) {
            setError('You must accept the terms and conditions to register.');
            return;
        }
        if (password.length < 12) {
            setError('Ο κωδικός πρέπει να έχει τουλάχιστον 12 χαρακτήρες.');
            return;
        }
        try {
            await registerUser(username, email, password, termsAccepted);
            setSuccess('Account created! Please check your email to verify your account before logging in.');
            setUsername(''); setEmail(''); setPassword(''); setTermsAccepted(false);
            setView('login');
        } catch (err) { setError(err.response?.data?.detail || 'Registration failed.'); }
    };

    const handleGoogleLogin = () => {
        window.location.href = '/api/auth/login/google';
    };

    const switchView = (newView) => {
        setView(newView);
        setError(''); setSuccess('');
    };

    return (
        <div className="login-page-wrapper">
            <div className="login-container">
                <h1>ALGOSTOCK CAPITAL</h1>
                <h2>SYSTEM ACCESS</h2>

                {view !== '2fa' && (
                    <div className="form-toggle">
                        <button onClick={() => switchView('login')} className={view === 'login' ? 'active' : ''}>Login</button>
                        <button onClick={() => switchView('register')} className={view === 'register' ? 'active' : ''}>Register</button>
                    </div>
                )}

                {success && <p className="success-message">{success}</p>}
                {error && <p className="error-message">{error}</p>}

                {view === 'login' && (
                     <form onSubmit={handleLogin} className="login-form">
                        <div className="input-group">
                            <FiUser className="input-icon" />
                            <input type="text" placeholder="Username or Email" value={usernameOrEmail} onChange={(e) => setUsernameOrEmail(e.target.value)} required />
                        </div>
                        <div className="input-group">
                            <FiLock className="input-icon" />
                            <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                        </div>
                        <button type="submit">Login</button>
                        <div className="auth-link">
                            <Link to="/forgot-password">Forgot password?</Link> | <Link to="/forgot-username">Forgot username?</Link>
                        </div>
                    </form>
                )}

                {view === 'register' && (
                     <form onSubmit={handleRegister} className="login-form">
                        <div className="input-group"><FiUser className="input-icon" /><input type="text" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} required /></div>
                        <div className="input-group"><FiMail className="input-icon" /><input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
                        <div className="input-group"><FiLock className="input-icon" /><input type="password" placeholder="Password (τουλάχιστον 12 χαρακτήρες)" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
                        <div className="form-group-checkbox">
                            <input type="checkbox" id="terms" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
                            <label htmlFor="terms">I agree to the <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</label>
                        </div>
                        <button type="submit">Create Account</button>
                    </form>
                )}

                {view === '2fa' && (
                    <form onSubmit={handle2FAVerification} className="login-form">
                        <p style={{color: 'var(--text-secondary)'}}>Enter the code from your authenticator app or a recovery code.</p>
                        <div className="input-group">
                            <FiShield className="input-icon" />
                            <input type="text" placeholder="6-digit or recovery code" value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value)} required />
                        </div>
                        <button type="submit">Verify</button>
                    </form>
                )}

                {view !== '2fa' && (
                    <>
                        <div className="divider"><span>OR</span></div>
                        <button className="btn-google" onClick={handleGoogleLogin}>
                            <GoogleIcon />
                            Sign In with Google
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default LoginPage;
